<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" persistent scrollable max-width="800px")
    yz-card
      yz-dialog-normal-header(:title="$t('panel.send_panel_claim')" @close="onClose")

      yz-dialog-normal-content.pa-6
        // Loading state
        template(v-if="isFetching")
          div(style="width: 600px; margin: auto;")
            yz-skeleton-loader(type="article")
            yz-skeleton-loader.mt-4(type="card")
            yz-skeleton-loader.mt-4(type="article")

        // Error state
        template(v-else-if="isError")
          yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('panel.unable_to_load_claim_details')" :description="$t('panel.check_claim_rid_and_retry')" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('retry')" @action-click="fetchData")

        // Main content
        template(v-else)
          div(style="width: 600px; margin: auto;")
            // Success message section
            .d-flex.align-center
              yz-icon.mr-2(size="20px" color="yz-success-500") $yzi-check-circle
              .yz-medium-body-sm.text-neutral-700 {{ $t('panel.panel_claim_created_successfully') }}

            // Instructions
            .yz-regular-body-sm.text-neutral-600.mt-2 {{ $t('panel.copy_message_instruction') }}

            // Message preview section
            .mt-6
              .d-flex.justify-space-between.align-center
                .yz-medium-body-sm.text-neutral-700 {{ $t('panel.message_preview') }}
                yz-btn(size="lg" secondary :label="$t('panel.copy_message')" append-icon="$yzi-copy-03" @click="onCopyMessage")

              yz-card.pa-4.mt-2(outlined, border-color="yz-neutral-400")
                div(style="width: 400px; margin: auto;")
                  .yz-regular-body-sm.text-neutral-700.preview-message(v-html="messageTemplate")

            // Panel details section
            yz-card.mt-4.pa-4(outlined, color="yz-neutral-200")
              v-row(no-gutters)
                // Left column
                v-col(cols="6")
                  .yz-medium-body-xs.text-neutral-500 {{ $t('panel.panel_details') }}

                // Right column
                v-col
                  // Panel name
                  .yz-medium-body-sm.text-neutral-700 {{ panelData.name }}

                  // Contact person
                  .d-flex.align-center.mt-1(v-if="panelData.contactPerson")
                    .yz-medium-body-xs.text-neutral-500 {{ panelData.contactPerson }}
                    yz-btn(icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(panelData.contactPerson)")

                  // Phone number
                  .d-flex.align-center.mt-n1(v-if="panelData.phone")
                    .yz-medium-body-xs.text-neutral-500 {{ panelData.phone }}
                    yz-btn(icon="mdi-whatsapp" icon-only size="sm" icon-color="yz-neutral-500" icon-size="15px" @click="onOpenWhatsApp")

                  // Email address
                  .d-flex.align-center.mt-n1(v-if="panelData.email")
                    .yz-medium-body-xs.text-neutral-500 {{ panelData.email }}
                    yz-btn(icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(panelData.email)")

      yz-dialog-normal-actions
        yz-btn(size="lg" :label="$t('panel.done')" :disabled="isFetching" @click="onClose")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { generatePanelClaimLink } from '@/utils'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    claimId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      // Page state
      isFetching: true,
      isError: false,
      // Data
      claim: null,
      single_claim_message: null,
      bulk_claim_message: null
    }
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    isSingleClaim() {
      return Boolean(this.claim && this.claim.type === 'single')
    },
    panelData() {
      if (!this.claim || !this.claim.panel) {
        return {
          name: '',
          contactPerson: '',
          phone: '',
          email: ''
        }
      }

      const panel = this.claim.panel
      return {
        name: panel.name || '',
        contactPerson: panel.pic_name || '',
        phone: panel.phone || '',
        email: panel.email || ''
      }
    },
    messageTemplate() {
      if (!this.claim) return ''

      // For single claims, use the custom template with variable mapping
      if (this.isSingleClaim && this.single_claim_message) {
        return this.mapTemplateVariables(this.single_claim_message)
      }

      // For bulk claims, use the custom bulk template with variable mapping
      if (!this.isSingleClaim && this.bulk_claim_message) {
        return this.mapTemplateVariables(this.bulk_claim_message)
      }

      return ''
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    mapTemplateVariables(template) {
      if (!template || !this.claim) return template

      // Get invoice data if available
      const invoice = this.claim.invoice || {}

      // Build variable mapping object
      const variables = {
        // Panel fields
        panel_name: this.claim.panel?.name || '',

        // Patient fields (single claim)
        patient_name: invoice?.name || '',
        patient_ic: invoice?.ic || '',

        // Staff/Employee fields
        staff_id: invoice?.staff_id || '',

        // Claim identification
        chit_number: invoice?.chit_number || '',
        claim_number: this.claim.rid || '',

        // Bulk claim specific fields
        total_invoices: this.claim.invoices.length || '0',

        // Financial fields
        claimed_amount: this.$options.filters.displayPrice(this.claim.submitted_amount || 0, this.$currency),

        // Date fields
        submission_date: this.claim.submitted_date ? this.formatDate(this.claim.submitted_date) : '',

        // Link
        claim_link: this.generateClaimLink(),

        // Medical fields (single claim)
        mc_days: invoice?.mc_days || '0',
        diagnosis: invoice?.diagnosis?.join(', ') || '',
        medication: invoice?.treatments?.map(treatment => treatment.name).join(', ') || '',

        // Clinic info
        clinic_name: this.company.name || ''
      }

      // Replace all template variables with actual values
      let result = template
      Object.keys(variables).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g')
        result = result.replace(regex, variables[key])
      })

      return result
    },
    generateClaimLink() {
      // Generate a link to view the claim in the system
      const companySlug = this.company?.slug || ''
      const claimPid = this.claim?.pid || ''
      return generatePanelClaimLink(companySlug, claimPid)
    },
    async fetchData() {
      if (!this.claimId) {
        this.isError = true
        this.isFetching = false
        return
      }

      this.isFetching = true
      this.isError = false

      try {
        const [claimResponse, claimMessageResponse] = await Promise.all([this.fetchClaimDetails(), this.fetchClaimMessage()])

        this.claim = claimResponse
        this.single_claim_message = claimMessageResponse.single_claim_message
        this.bulk_claim_message = claimMessageResponse.bulk_claim_message
      } catch (error) {
        console.error('Error fetching data:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async fetchClaimDetails() {
      const { data } = await this.$axios.get(`/panels/${this.company.slug}/claims/${this.claimId}`)

      // For single claims, fetch full invoice details
      if (data.type === 'single' && data.invoices?.length > 0) {
        try {
          const invoiceUid = data.invoices[0]
          const invoiceResponse = await this.$axios.get(`/panels/${this.company.slug}/invoices/${invoiceUid}`)
          // Replace UID string with full invoice object
          data.invoice = invoiceResponse.data
        } catch (error) {
          console.error('Error fetching invoice details:', error)
          this.isError = true
        }
      }

      return data
    },
    async fetchClaimMessage() {
      const { data } = await this.$axios.get(`/clinics/${this.company.slug}`)
      return data
    },
    onClose() {
      this.$emit('input', false)
      this.$emit('close')
    },
    async onCopyMessage() {
      try {
        const htmlContent = this.messageTemplate
        const plainText = this.convertHtmlToPlainText(htmlContent)

        // Try to copy both formats (modern browsers)
        if (navigator.clipboard && window.ClipboardItem) {
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
          const textBlob = new Blob([plainText], { type: 'text/plain' })
          // eslint-disable-next-line no-undef
          const clipboardItem = new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob
          })
          await navigator.clipboard.write([clipboardItem])
        } else {
          // Fallback to plain text only
          await this.onCopyText(plainText)
          return
        }

        await this.showMessage(['success', this.$t('panel.message_copied')])
      } catch (err) {
        console.error('Failed to copy message: ', err)
        // Final fallback
        try {
          const plainText = this.convertHtmlToPlainText(this.messageTemplate)
          await this.onCopyText(plainText)
        } catch (fallbackErr) {
          await this.showMessage(['error', this.$t('panel.failed_copy_message')])
        }
      }
    },
    convertHtmlToPlainText(html) {
      if (!html) return ''

      // Create a temporary element to parse HTML
      const temp = document.createElement('div')
      temp.innerHTML = html

      // Convert headings, <br> and </p> to newlines before stripping tags
      let text = html
        .replace(/<\/h[1-6]>/gi, '\n')
        .replace(/<h[1-6][^>]*>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<p>/gi, '')

      // Strip all remaining HTML tags
      temp.innerHTML = text
      text = temp.textContent || temp.innerText || ''

      // Clean up extra whitespace while preserving intentional line breaks
      text = text
        .split('\n')
        .map(line => line.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines

      return text.trim()
    },
    async onCopyText(text) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback for browsers that don't support clipboard API
          const textArea = document.createElement('textarea')
          textArea.value = text
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        await this.showMessage(['success', this.$t('panel.text_copied')])
      } catch (err) {
        console.error('Failed to copy text: ', err)
        await this.showMessage(['error', this.$t('panel.failed_copy_text')])
      }
    },
    onOpenWhatsApp() {
      const phoneNumber = this.$options.filters.phoneSanitized(this.panelData.phone)
      const message = encodeURIComponent(this.convertHtmlToPlainText(this.messageTemplate))
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`
      window.open(whatsappUrl, '_blank')
    },
    formatDate(dateString) {
      try {
        const date = new Date(dateString)
        const day = date
          .getDate()
          .toString()
          .padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        const minutes = date
          .getMinutes()
          .toString()
          .padStart(2, '0')
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM'
        const displayHours = date.getHours() % 12 || 12

        return `${day}/${month}/${year}, ${displayHours}:${minutes} ${ampm}`
      } catch (error) {
        console.error('Error formatting date:', error)
        return dateString
      }
    }
  }
}
</script>

<style scoped>
::v-deep .preview-message p {
  margin-bottom: 0;
}
</style>
